// Contrail V3.7.0 / V3.9.0 (icon = show/hide on map) — mobile Objects tab: search, status chips, group filter. Tap a row → map + object sheet.
import { useEffect, useMemo, useState } from "react";
import { supabase, type Device } from "../lib/supabase";
import { ago, liveSpeed, statusOf, STATUS_LABEL, type Status } from "../lib/format";
import { iconSvg } from "../lib/icons";
import { toggleHidden, useUserPrefs } from "../lib/prefs";

export default function ObjectList({ devices, now, filter, onFilter, onPick }: {
  devices: Device[]; now: number; filter: Status | null; onFilter: (s: Status | null) => void; onPick: (id: string) => void;
}) {
  const [q, setQ] = useState("");
  const hidden = new Set(useUserPrefs().hidden_devices);   // V3.9.0: tap the icon to hide / show on the map
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [group, setGroup] = useState("");
  useEffect(() => { supabase.from("device_groups").select("id, name").order("name").then(({ data }) => setGroups(data ?? [])); }, []);
  const rows = useMemo(() => devices.filter((d) => {
    const st = statusOf(d.state, now);
    if (filter && st !== filter && !(filter === "offline" && st === "nodata")) return false;
    if (group && (group === "none" ? d.group_id : d.group_id !== group)) return false;
    const t = q.trim().toLowerCase();
    return !t || d.name.toLowerCase().includes(t) || d.unique_id.toLowerCase().includes(t);
  }), [devices, now, filter, group, q]);
  const chips: [Status | null, string][] = [[null, "All"], ["moving", "Moving"], ["stopped", "Stopped"], ["late", "No signal"], ["offline", "Offline"]];

  return (
    <div className="m-panel">
      <div className="m-panel-head">
        <h1>Objects</h1>
        <input className="m-search" type="search" placeholder="Search name or ID" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search objects" />
        <div className="m-chips" role="group" aria-label="Filter by status">
          {chips.map(([k, l]) => <button key={l} type="button" className={filter === k ? "on" : ""} aria-pressed={filter === k} onClick={() => onFilter(k)}>{l}</button>)}
        </div>
        {groups.length > 0 && (
          <select className="m-select" value={group} onChange={(e) => setGroup(e.target.value)} aria-label="Filter by group">
            <option value="">All groups</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            <option value="none">No group</option>
          </select>
        )}
      </div>
      <ul className="m-list">
        {rows.map((d) => {
          const s = d.state, st = statusOf(s, now);
          return (
            <li key={d.id} className={hidden.has(d.id) ? "off-map" : ""}>
              <button type="button" className={`m-licon-btn ${hidden.has(d.id) ? "off" : ""}`} aria-pressed={!hidden.has(d.id)}
                aria-label={hidden.has(d.id) ? `Show ${d.name} on the map` : `Hide ${d.name} on the map`}
                title={hidden.has(d.id) ? "Hidden on the map — tap to show" : "Tap to hide on the map"}
                onClick={() => toggleHidden(d.id).catch(() => { /* rolled back */ })}>
                <span className={`licon ${st}`} dangerouslySetInnerHTML={{ __html: iconSvg(d.icon, 14) }} />
              </button>
              <button type="button" className="m-row-btn" onClick={() => onPick(d.id)} disabled={s?.lat == null}>
                <span className="m-row-main"><b>{d.name}</b><span className="sub">{STATUS_LABEL[st]} · {ago(s?.seen_at, now)}</span></span>
                <span className="m-row-speed mono">{liveSpeed(s, st)}</span>
              </button>
            </li>
          );
        })}
        {!rows.length && <li className="m-empty">{devices.length ? "Nothing matches." : "No objects yet."}</li>}
      </ul>
    </div>
  );
}
